// src/oura/components/newscan/BranchesCard.tsx
//
// "Which branches does <business> have?" on a First AI check's Review: one
// list in place of the outlet cards (newscan/branches.ts), Oura's guess
// ticked, a reason beside each doubtful name, "Add a branch", the website's
// names that are not places folded into one line, and the quote's count
// beside the description (beside the button it read as a second button). "Use these 2 branches" runs the cards' own writes one at
// a time until the draft matches, showing them as three steps that tick in
// turn (Jack, 19 Oct 2026: the page "jumps really fast"), with the card held
// where it is on screen. It then says what changed, with Undo and Change
// (no "Got it": the branches are set already), and reads as one line with
// Change once the Review is left and opened again.
//
// Tell Oura (TellOura.tsx) sits above it and runs its answers through the
// same applier, so both read as steps and undo as one.

import { CheckIcon, CircleHelpIcon, PlusIcon, XIcon } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { DecisionChoice, ScanDraft } from "@service/aeo/scanDraftTypes";
import { cn } from "@oura/lib/cn";
import { IconSquare, PillButton, SURFACE, TYPE } from "@oura/ui";
import { DecisionCard } from "./DecisionCard";
import { useScanFlow } from "@oura/newscan/ScanFlowContext";
import { plural } from "@oura/newscan/copy";
import { activeBranches, branchRows, nextBranchStep, openBranchCards, branchesTitle } from "@oura/newscan/branches";
import { useWritesPaused } from "@context/maintenance";

const COPY = {
    title: branchesTitle,
    detail: "Tick the places customers can walk into. The 26 location questions are split across them.",
    add: "Add a branch",
    skipped: (n: number) => `${plural(n, "other name")} on the website ${n === 1 ? "is" : "are"} not a place, so Oura left ${n === 1 ? "it" : "them"} out`,
    skippedOpen: "On the website, not places:",
    applyLabel: (n: number) => `Use ${n === 1 ? "this branch" : `these ${n} branches`}`,
    matches: (q: number) => `Matches the quote (${q})`,
    quote: (q: number, n: number) => `The quote paid for ${q}. ${n > q ? "Ticking more adds branches the client did not buy." : "Ticking fewer leaves paid branches out."}`,
    working: "Updating the 50",
    phaseDrop: (names: string[]) => `Stop asking about ${names.join(" and ")}`,
    phaseKeep: (names: string[]) => `Keep ${names.join(" and ")}`,
    phaseRewrite: (names: string[]) => `Rewrite the location questions for ${names.join(" and ")}`,
    phasePlan: "Match the quote and the plan's branch names",
    resultTitle: (names: string[]) => `Branches set: ${names.join(" and ")}.`,
    resultDropped: (names: string[]) => `${names.join(" and ")} ${names.length === 1 ? "is" : "are"} no longer asked about.`,
    resultQuote: (q: number, n: number) => (n === q ? `Matches the quote (${q}).` : `The quote paid for ${q}.`),
    resultLeft: (before: number, after: number) =>
        after === 0 ? `Nothing left to decide (was ${before}).` : `${plural(after, "decision")} left, down from ${before}.`,
    byTell: "Tell Oura is updating the branches",
    done: (names: string[]) => `Branches: ${names.join(" and ")}.`,
    split: (n: number) => (n === 2 ? "The 26 location questions are split 14 and 12." : n > 1 ? `The 26 location questions are split across ${n}.` : "All 14 location questions ask about it."),
    change: "Change",
    undo: "Undo",
    cancel: "Cancel",
    stuck: "Oura could not finish this. Check the outlets on the Business step.",
} as const;

/** One of the cards' own writes, run in turn with the branches (Tell Oura's other changes). */
export type ApplyOp =
    | { kind: "answer"; decisionId: string; choice: DecisionChoice; label: string }
    | { kind: "plan"; decisionId: string; values: string[]; label: string };

/** What one answer is changing, kept for the steps and the result. */
export interface ApplyPlan {
    /** The draft the plan was made for. Its writes never land on another one (Jack, #4501). */
    draftId: string;
    /** Who asked: the list's button, or Tell Oura. Each shows the steps and the result in its own place. */
    origin: "card" | "tell";
    /** The branches to end with. Null: the branches stay as they are. */
    target: string[] | null;
    dropped: string[];
    /** Writes before the branches (a core service) and after them (the plan's keywords, which name the branches). */
    pre: ApplyOp[];
    post: ApplyOp[];
    /** The steps staff see, in order. */
    steps: string[];
    /** Decisions left when staff pressed the button, for "down from 9". */
    before: number;
}

/** The three branch steps. The writes behind them are many; each lands in one. */
const phaseOf = (step: NonNullable<ReturnType<typeof nextBranchStep>>): 0 | 1 | 2 =>
    step.kind === "rebuild" ? 1 : step.kind === "plan" || (step.kind === "answer" && step.label === "quote") ? 2 : 0;

/** Long enough to read a ticked step before the next one starts. */
const STEP_MS = 700;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Runs the writes one answer needs, one at a time, as readable steps, and undoes them as one. */
function useApplyBranches(draft: ScanDraft | null) {
    const { ctl } = useScanFlow();
    const writesPaused = useWritesPaused();
    const [plan, setPlan] = useState<ApplyPlan | null>(null);
    const [phase, setPhase] = useState(0);
    const [finishing, setFinishing] = useState(false);
    const [result, setResult] = useState<ApplyPlan | null>(null);
    const [stuck, setStuck] = useState(false);
    const last = useRef<string>("");
    const phaseRef = useRef(0);
    const inFlight = useRef(false);
    const preAt = useRef(0);
    const postAt = useRef(0);
    const [, bump] = useState(0);
    // The writes one answer made: Undo takes them all back, so the answer undoes as one step.
    const [writes, setWrites] = useState(0);
    const [undoing, setUndoing] = useState(false);
    // Who made the writes Undo would take back: only that place offers Undo.
    const [origin, setOrigin] = useState<ApplyPlan["origin"] | null>(null);
    // A batch, its result and its Undo belong to one draft: another draft starts clean (Jack, #4451).
    const draftId = draft?.id ?? null;
    // The draft on screen now, read after each await: the reset below runs a render late, so it
    // cannot be what stops an old plan writing into a new draft.
    const currentDraftId = useRef<string | null>(draftId);
    currentDraftId.current = draftId;
    useEffect(() => {
        setPlan(null);
        setPhase(0);
        setFinishing(false);
        setResult(null);
        setStuck(false);
        setWrites(0);
        setOrigin(null);
        setCompletedVersion(null);
        last.current = "";
        phaseRef.current = 0;
        preAt.current = 0;
        postAt.current = 0;
        inFlight.current = false;
    }, [draftId]);
    // The draft version the batch ended at. Undo is offered only while the draft is still there:
    // any later write (a question edit, a card) is the newest undo step, and undoing the batch
    // would take that one back first (Jack, #4451).
    const [completedVersion, setCompletedVersion] = useState<number | null>(null);

    useEffect(() => {
        if (!plan || !draft || plan.draftId !== draft.id || ctl.busy || finishing || inFlight.current) return;
        const isOpen = (id: string) => draft.decisions.some((d) => d.id === id && d.state === "open");
        const branchBase = plan.pre.length;
        const postBase = branchBase + (plan.target ? 3 : 0);

        let index = -1;
        let write: (() => Promise<unknown>) | null = null;
        if (preAt.current < plan.pre.length) {
            const op = plan.pre[preAt.current];
            index = preAt.current;
            preAt.current += 1;
            if (isOpen(op.decisionId)) write = () => runOp(op);
        } else {
            const step = plan.target ? nextBranchStep(draft, plan.target) : null;
            if (step) {
                // The same step twice means the write did not move the draft: stop instead of looping.
                const key = `${draft.version}:${JSON.stringify(step)}`;
                if (key === last.current) {
                    setPlan(null);
                    setStuck(true);
                    return;
                }
                last.current = key;
                index = branchBase + phaseOf(step);
                write = () =>
                    step.kind === "answer"
                        ? ctl.answer(step.decisionId, step.choice)
                        : step.kind === "outlets"
                          ? ctl.patchBusiness({ outlets: step.outlets })
                          : step.kind === "rebuild"
                            ? ctl.rebuild()
                            : ctl.answerPlan(step.decisionId, step.values);
            } else if (postAt.current < plan.post.length) {
                const op = plan.post[postAt.current];
                index = postBase + postAt.current;
                postAt.current += 1;
                if (isOpen(op.decisionId)) write = () => runOp(op);
            } else {
                const completed = plan;
                const versionAtCompletion = draft.version;
                setPhase(plan.steps.length);
                setFinishing(true);
                void sleep(STEP_MS).then(() => {
                    if (currentDraftId.current !== completed.draftId) return;
                    setResult(completed);
                    setCompletedVersion(versionAtCompletion);
                    setPlan(null);
                    setFinishing(false);
                    last.current = "";
                });
                return;
            }
        }

        function runOp(op: ApplyOp) {
            return op.kind === "answer" ? ctl.answer(op.decisionId, op.choice) : ctl.answerPlan(op.decisionId, op.values);
        }

        inFlight.current = true;
        const planDraftId = plan.draftId;
        const go = async () => {
            if (index > phaseRef.current) {
                // Let the step just finished show its tick first.
                phaseRef.current = index;
                setPhase(index);
                await sleep(STEP_MS);
            }
            if (currentDraftId.current !== planDraftId) return;
            const fail = write ? await write() : null;
            if (currentDraftId.current !== planDraftId) return;
            inFlight.current = false;
            if (fail) {
                setPlan(null);
                setStuck(true);
            } else if (write) setWrites((w) => w + 1);
            else bump((n) => n + 1);
        };
        void go();
    }, [draft, plan, ctl, finishing]);

    return {
        running: plan !== null,
        plan,
        /** Every write landed: the last step shows its tick before the result. */
        finishing,
        /** The step running now, an index into `plan.steps`. */
        phase,
        result,
        stuck,
        apply: (spec: { origin: ApplyPlan["origin"]; target: string[] | null; pre?: ApplyOp[]; post?: ApplyOp[]; before: number }) => {
            if (!draft || writesPaused) return;
            const active = activeBranches(draft).map((o) => o.name);
            const lower = (spec.target ?? active).map((x) => x.toLowerCase());
            const dropped = spec.target ? active.filter((a) => !lower.includes(a.toLowerCase())) : [];
            const pre = spec.pre ?? [];
            const post = spec.post ?? [];
            const branchSteps = spec.target
                ? [dropped.length ? COPY.phaseDrop(dropped) : COPY.phaseKeep(spec.target), COPY.phaseRewrite(spec.target), COPY.phasePlan]
                : [];
            setStuck(false);
            setResult(null);
            last.current = "";
            phaseRef.current = 0;
            preAt.current = 0;
            postAt.current = 0;
            inFlight.current = false;
            setPhase(0);
            setWrites(0);
            setCompletedVersion(null);
            setOrigin(spec.origin);
            setPlan({ ...spec, draftId: draft.id, pre, post, dropped, steps: [...pre.map((o) => o.label), ...branchSteps, ...post.map((o) => o.label)] });
        },
        dismiss: () => setResult(null),
        /** Writes the last answer made, while they are still the newest: nothing has been written since. */
        canUndo:
            writes > 0 &&
            plan === null &&
            completedVersion !== null &&
            draft?.version === completedVersion &&
            (draft?.undo.available ?? 0) >= writes,
        /** Where the writes Undo would take back came from. */
        origin,
        undoing,
        /**
         * Takes the batch back. True only when every write was undone. On a failure the rest stay
         * counted and Undo stays offered: the batch is re-anchored to the version the last
         * successful undo produced (the server's, not a guess), so staff can press it again (Jack, #4451).
         */
        undoAll: async (): Promise<boolean> => {
            if (completedVersion === null || writesPaused) return false;
            setUndoing(true);
            let remaining = writes;
            let anchor = completedVersion;
            try {
                while (remaining > 0) {
                    const fail = await ctl.undo(undefined, (landed) => {
                        anchor = landed.version;
                    });
                    if (fail) break;
                    remaining -= 1;
                }
                setWrites(remaining);
                if (remaining > 0) {
                    setCompletedVersion(anchor);
                    return false;
                }
                setResult(null);
                setOrigin(null);
                setCompletedVersion(null);
                return true;
            } finally {
                setUndoing(false);
            }
        },
    };
}

export type Applier = ReturnType<typeof useApplyBranches>;

/** The steps of a running answer, each ticking in turn. */
export function ApplySteps({ applier }: { applier: Applier }) {
    const steps = applier.plan?.steps ?? [];
    return (
        <ol role="list" className="list-plain m-0 space-y-2.5 p-0">
            {steps.map((text, i) => {
                const done = applier.phase > i || applier.finishing;
                const now = applier.phase === i && !done;
                return (
                    <li key={i} className={cn(TYPE.tb, "flex items-center gap-3 transition-colors duration-300", done || now ? "text-gray-900" : "text-gray-400")}>
                        {done ? (
                            <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-green-600 text-white">
                                <CheckIcon aria-hidden size={12} strokeWidth={3} />
                            </span>
                        ) : now ? (
                            <span className="size-5 shrink-0 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" aria-hidden />
                        ) : (
                            <span className="size-5 shrink-0 rounded-full border-2 border-gray-200" aria-hidden />
                        )}
                        {text}
                    </li>
                );
            })}
        </ol>
    );
}

/** Keeps `el` at the same height on screen while the page above and below it changes. */
export function useHoldInPlace(el: React.RefObject<HTMLElement | null>, active: boolean) {
    const top = useRef<number | null>(null);
    useLayoutEffect(() => {
        const node = el.current;
        if (!active || !node) {
            top.current = null;
            return;
        }
        const now = node.getBoundingClientRect().top;
        if (top.current === null) {
            top.current = now;
            return;
        }
        const delta = now - top.current;
        if (Math.abs(delta) < 1) return;
        let p: HTMLElement | null = node.parentElement;
        while (p && !(p.scrollHeight > p.clientHeight && /auto|scroll/.test(getComputedStyle(p).overflowY))) p = p.parentElement;
        if (p) p.scrollTop += delta;
        else window.scrollBy(0, delta);
    });
}

export function BranchesCard({
    draft,
    readOnly,
    applier,
    left,
    headless = false,
}: {
    draft: ScanDraft;
    readOnly: boolean;
    applier: Applier;
    /** Decisions left on the Review now. */
    left: number;
    /** The section above already asks the question: the card's title is for screen readers only. */
    headless?: boolean;
}) {
    const list = useMemo(() => branchRows(draft), [draft]);
    const writesPaused = useWritesPaused();
    const box = useRef<HTMLElement>(null);
    useHoldInPlace(box, applier.plan?.origin === "card" || applier.result?.origin === "card");
    const open = openBranchCards(draft).length > 0;
    const [editing, setEditing] = useState(false);
    const showList = (open || editing) && !readOnly;

    const [ticked, setTicked] = useState<string[]>(() => list.rows.filter((r) => r.guess).map((r) => r.name));
    const [added, setAdded] = useState<string[]>([]);
    const [typing, setTyping] = useState("");
    const [showSkipped, setShowSkipped] = useState(false);
    // A fresh guess when the list itself changes (another write, Undo).
    const rowsKey = list.rows.map((r) => `${r.name}:${r.guess}`).join("|");
    useEffect(() => {
        setTicked(list.rows.filter((r) => r.guess).map((r) => r.name));
        setAdded([]);
    }, [rowsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    if (applier.plan?.origin === "card") {
        return (
            <section ref={box} className={cn(SURFACE.block)} aria-live="polite" aria-busy="true">
                <p className={cn(TYPE.tb, "m-0 mb-3 font-semibold text-gray-900")}>{COPY.working}</p>
                <ApplySteps applier={applier} />
            </section>
        );
    }
    if (applier.plan?.target) {
        // Tell Oura is changing the branches: its own panel shows the steps.
        return (
            <section data-tell="branches" className={cn(SURFACE.block, "flex items-center gap-3")} aria-busy="true">
                <span className="size-4 shrink-0 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" aria-hidden />
                <p className={cn(TYPE.tb, "m-0 text-gray-600")}>{COPY.byTell}</p>
            </section>
        );
    }

    // Set: the same answered card as "For the plan" (green tick, title, what it means, text actions),
    // whether it was set just now (with what changed) or before (Jovi, 20 Oct).
    if ((applier.result?.origin === "card" && !readOnly) || !showList) {
        const r = applier.result?.origin === "card" && !readOnly ? applier.result : null;
        const names = activeBranches(draft).map((o) => o.name);
        const q = list.quote;
        const actions = readOnly
            ? []
            : [
                  ...(applier.canUndo && (r || applier.origin === "card")
                      ? [{ key: "undo", label: COPY.undo, kind: "text" as const, busy: applier.undoing, disabled: writesPaused, onClick: () => void applier.undoAll() }]
                      : []),
                  {
                      key: "change",
                      label: COPY.change,
                      kind: "text" as const,
                      movesOnly: true,
                      onClick: () => {
                          if (r) applier.dismiss();
                          setTicked(names);
                          setEditing(true);
                      },
                  },
              ];
        return (
            <section ref={box} data-tell="branches" aria-live="polite">
                <DecisionCard
                    severity="info"
                    icon={CheckIcon}
                    tone="green"
                    level={3}
                    title={COPY.done(names)}
                    detail={[q !== null ? COPY.resultQuote(q, names.length) : null, COPY.split(names.length)].filter(Boolean).join(" ")}
                    actions={actions}
                >
                    {r && (r.dropped.length > 0 || r.before !== left) && (
                        <ul role="list" className={cn(TYPE.tc, "m-0 list-disc space-y-0.5 pl-4 text-gray-600")}>
                            {r.dropped.length > 0 && <li>{COPY.resultDropped(r.dropped)}</li>}
                            <li>{COPY.resultLeft(r.before, left)}</li>
                        </ul>
                    )}
                </DecisionCard>
            </section>
        );
    }

    const rows = [...list.rows, ...added.map((name) => ({ name, from: "Added by you", guess: true, doubt: null, active: false }))];
    const n = ticked.length;
    const q = list.quote;
    const toggle = (name: string) => setTicked((t) => (t.includes(name) ? t.filter((x) => x !== name) : [...t, name]));
    const addTyped = () => {
        const name = typing.trim().replace(/\s+/g, " ");
        if (!name || rows.some((r) => r.name.toLowerCase() === name.toLowerCase())) return;
        setAdded((a) => [...a, name]);
        setTicked((t) => [...t, name]);
        setTyping("");
    };
    // Keep the flagship first: the list's order, the ticked ones only.
    const ordered = rows.map((r) => r.name).filter((name) => ticked.includes(name));

    return (
        <section data-tell="branches" aria-labelledby="branches-title" className={cn(SURFACE.block, "flex items-start gap-3")}>
            <IconSquare tone="amber" icon={CircleHelpIcon} />
            <div className="min-w-0 flex-1">
                <h3 id="branches-title" className={cn(TYPE.tb, "m-0 pt-1.5 font-semibold text-gray-900", headless && "sr-only")}>
                    {COPY.title(draft.business.businessName)}
                </h3>
                <div className={cn("flex flex-wrap items-start justify-between gap-x-4 gap-y-1", headless ? "pt-1.5" : "mt-1")}>
                    <p className={cn(TYPE.tc, "m-0 min-w-0 flex-1 basis-56 text-gray-600")}>{COPY.detail}</p>
                    {/* A status beside the list, not next to the button, where it read as one (Jovi, 20 Oct). */}
                    {q !== null && (
                        <span className={cn(TYPE.tc, "inline-flex shrink-0 items-center gap-1 font-medium", n === q ? "text-green-700" : "text-amber-700")}>
                            {n === q && <CheckIcon aria-hidden size={13} strokeWidth={2.5} />}
                            {n === q ? COPY.matches(q) : COPY.quote(q, n)}
                        </span>
                    )}
                </div>

                <ul role="list" className="list-plain m-0 mt-3 divide-y divide-gray-100 border-y border-gray-100 p-0">
                    {rows.map((r) => {
                        const on = ticked.includes(r.name);
                        return (
                            <li key={r.name}>
                                <label className="flex cursor-pointer items-start gap-3 py-2.5">
                                    <Tick checked={on} onChange={() => toggle(r.name)} />
                                    <span className="min-w-0 flex-1">
                                        <span className={cn(TYPE.tb, "block font-medium transition-colors", on ? "text-gray-900" : "text-gray-500")}>{r.name}</span>
                                        <span className={cn(TYPE.tc, "block", r.doubt ? "text-amber-700" : "text-gray-400")}>{r.doubt ?? r.from}</span>
                                    </span>
                                </label>
                            </li>
                        );
                    })}
                    <li className="flex items-center gap-3 py-1">
                        <PlusIcon aria-hidden size={18} strokeWidth={1.75} className="shrink-0 text-gray-400" />
                        <input
                            id="branches-add"
                            aria-label={COPY.add}
                            value={typing}
                            onChange={(e) => setTyping(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    addTyped();
                                }
                            }}
                            placeholder={COPY.add}
                            className={cn(TYPE.tb, "min-w-0 flex-1 bg-transparent py-1.5 text-gray-900 outline-none placeholder:text-gray-400")}
                        />
                        {typing.trim() && (
                            <PillButton variant="text" size="sm" onClick={addTyped}>
                                Add
                            </PillButton>
                        )}
                    </li>
                </ul>

                {list.skipped.length > 0 && (
                    <div className={cn(TYPE.tc, "mt-2 text-gray-500")}>
                        {showSkipped ? (
                            <p className="m-0 flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span>{COPY.skippedOpen}</span>
                                {list.skipped.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        title={`Add ${s} as a branch`}
                                        onClick={() => {
                                            setAdded((a) => (a.includes(s) ? a : [...a, s]));
                                            setTicked((t) => (t.includes(s) ? t : [...t, s]));
                                        }}
                                        className="rounded-full border-0 bg-white px-2 py-0.5 text-gray-600 ring-1 ring-black/[0.06] hover:text-gray-900"
                                    >
                                        {s} +
                                    </button>
                                ))}
                                <button type="button" onClick={() => setShowSkipped(false)} className="border-0 bg-transparent p-0 text-gray-400" aria-label="Hide">
                                    <XIcon size={13} aria-hidden />
                                </button>
                            </p>
                        ) : (
                            <button type="button" onClick={() => setShowSkipped(true)} className="border-0 bg-transparent p-0 text-left text-gray-500 underline-offset-2 hover:underline">
                                {COPY.skipped(list.skipped.length)}
                            </button>
                        )}
                    </div>
                )}

                <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2">
                    <PillButton variant="primary" size="sm" writes disabled={n === 0} onClick={() => { setEditing(false); applier.apply({ origin: "card", target: ordered, before: left }); }}>
                        {COPY.applyLabel(n)}
                    </PillButton>
                    {editing && !open && (
                        <PillButton variant="text" size="sm" onClick={() => setEditing(false)}>
                            {COPY.cancel}
                        </PillButton>
                    )}
                </div>
                {applier.stuck && <p className={cn(TYPE.tc, "m-0 mt-2 text-rose-700")}>{COPY.stuck}</p>}
            </div>
        </section>
    );
}

/** A quiet checkbox: a rounded square that fills blue with a white tick. */
export function Tick({ checked, onChange, label }: { checked: boolean; onChange(): void; label?: string }) {
    return (
        <span className="relative mt-px inline-flex size-[18px] shrink-0">
            <input
                type="checkbox"
                aria-label={label}
                checked={checked}
                onChange={onChange}
                className="peer m-0 size-[18px] cursor-pointer appearance-none rounded-[5px] border border-gray-300 bg-white transition-colors checked:border-blue-600 checked:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500"
            />
            <CheckIcon aria-hidden size={12} strokeWidth={3} className="pointer-events-none absolute left-[3px] top-[3px] text-white opacity-0 transition-opacity peer-checked:opacity-100" />
        </span>
    );
}

export { useApplyBranches };
